import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { ViewMode } from "../texte";

export type Role = "admin" | "student";
export type Origin = "created" | "redeemed";

export type LastKnown = {
  state: string; // ready | planned | running | waiting | budget | done | error
  detail?: string;
  updatedAt: string;
  budget?: { total: number; used: number };
};

export type WorkspaceEntry = {
  workspace: string;
  token: string;
  /**
   * UI-Rolle für diesen Arbeitsbereich. WICHTIG: Der Server (awschat/plugins/botagent.py)
   * kennt aktuell keine admin/student-Unterscheidung für Workspaces — jedes gültige Token
   * darf jeden Aufruf machen (anders als bei den Users-Gruppen `admin`/`sandbox` an anderer
   * Stelle im System). "role" ist daher rein clientseitige UI-Gating-Information, kein
   * serverseitig durchgesetztes Recht: wer einen Arbeitsbereich anlegt (origin:"created"),
   * gilt hier als "admin"; wer nur einen Zugangslink einlöst (origin:"redeemed"), als
   * "student". Echte Zugriffskontrolle müsste serverseitig ergänzt werden.
   */
  role: Role;
  view: ViewMode;
  origin: Origin;
  addedAt: number;
  lastKnown?: LastKnown;
  notifiedQids: string[];
  /**
   * Ob die Experte-Sicht für Lernende in diesem Arbeitsbereich freigegeben ist
   * (client.md §12, config.yml-Schlüssel `expert_enabled`). Der Wert wird vom Server
   * synchronisiert (via Workspace.tsx/configQuery); als Backup (und Fast-Path beim ersten
   * Betreten) kann er auch in den Zugangslink kodiert werden (`#/ws/<token>?expert=1`, siehe
   * Dialogs.tsx/App.tsx) und beim Einlösen einmalig in den lokalen Eintrag übernommen werden.
   */
  expertEnabled: boolean;
};

export type WorkspacesState = {
  order: string[];
  byName: Record<string, WorkspaceEntry>;
  upsert: (entry: WorkspaceEntry) => void;
  remove: (workspace: string) => void;
  setLastKnown: (workspace: string, lastKnown: LastKnown) => void;
  setView: (workspace: string, view: ViewMode) => void;
  setExpertEnabled: (workspace: string, on: boolean) => void;
  addNotifiedQid: (workspace: string, qid: string) => void;
  byToken: (token: string) => WorkspaceEntry | undefined;
};

/** localStorage "botagent.workspaces.v1" — client.md §6 */
export const useWorkspaces = create<WorkspacesState>()(
  persist(
    (set, get) => ({
      order: [],
      byName: {},
      upsert: (entry) =>
        set((s) => {
          const exists = !!s.byName[entry.workspace];
          return {
            byName: { ...s.byName, [entry.workspace]: entry },
            order: exists ? s.order : [...s.order, entry.workspace],
          };
        }),
      remove: (workspace) =>
        set((s) => {
          const next = { ...s.byName };
          delete next[workspace];
          return { byName: next, order: s.order.filter((w) => w !== workspace) };
        }),
      setLastKnown: (workspace, lastKnown) =>
        set((s) => {
          const entry = s.byName[workspace];
          if (!entry) return s;
          return { byName: { ...s.byName, [workspace]: { ...entry, lastKnown } } };
        }),
      setView: (workspace, view) =>
        set((s) => {
          const entry = s.byName[workspace];
          if (!entry) return s;
          return { byName: { ...s.byName, [workspace]: { ...entry, view } } };
        }),
      setExpertEnabled: (workspace, on) =>
        set((s) => {
          const entry = s.byName[workspace];
          if (!entry) return s;
          return { byName: { ...s.byName, [workspace]: { ...entry, expertEnabled: on } } };
        }),
      addNotifiedQid: (workspace, qid) =>
        set((s) => {
          const entry = s.byName[workspace];
          if (!entry || entry.notifiedQids.includes(qid)) return s;
          return {
            byName: {
              ...s.byName,
              [workspace]: { ...entry, notifiedQids: [...entry.notifiedQids, qid] },
            },
          };
        }),
      byToken: (token) => Object.values(get().byName).find((w) => w.token === token),
    }),
    { name: "botagent.workspaces.v1" },
  ),
);
